import { createSlice } from "@reduxjs/toolkit";
import {
  getDietPlan,
  getDatesToDisable,
  getDietPlanV1,
  getFoodReplacements,
} from "../actions";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";

dayjs.extend(utc);

// ... (Keep existing consts like today, todayLessAWeek etc.)
const today = dayjs().utc(true).format("YYYY-MM-DD");
const todayLessAWeek = dayjs()
  .utc(true)
  .subtract(7, "day")
  .format("YYYY-MM-DD");
const todayPlusAWeek = dayjs().utc(true).add(7, "day").format("YYYY-MM-DD");
const defaultDisabled = [];
let day = todayLessAWeek;
while (day <= todayPlusAWeek) {
  defaultDisabled.push(day);
  day = dayjs(day).add(1, "day").format("YYYY-MM-DD");
}

const initialState = {
  dietPlan: null,
  isLoading: false,
  error: false,
  dates: [null, null],
  dietPlanV1: {
    dietPlan: null,
    shoppingList: null,
    isLoading: false,
    error: false,
    // HTTP status behind `error`, so the page can tell "this day has no menu"
    // (400) from "we could not load it" (403/5xx/offline) — they used to read
    // identically and both showed "Menu non disponibile".
    errorStatus: null,
    errorCode: null,
    lastFetched: null,
    lastFetchedShoppingList: null,
    // The planMode that was active when lastFetched was set.
    // Used to detect stale data when the user switches between MC and adoc.
    lastFetchedPlanMode: null,
    // Only the latest in-flight getDietPlanV1 may write results. Without this,
    // a slow Master Club response can land after an Adoc switch and paint the
    // MC sample menu on top of Adoc mode.
    currentRequestId: null,
  },
  getDatesToDisable: {
    isLoading: false,
    error: false,
    daysLimit: [today, today],
    disableDates: defaultDisabled,
    routes: [],
  },
  // Plan mode: 'master_club' (default) or 'adoc'.
  // Auto-set to 'adoc' on load when the user is an adoc patient and has a plan
  // for today; kept across calendar navigation once set.
  planMode: 'master_club',
  // Replacements State
  replacements: [],
  replacementsMeta: {
    current_page: 1,
    last_page: 1,
    total: 0,
    user_has_favorites: false,
  },
  replacementsLoading: false,
  replacementsError: null,
  replacementsFilters: null,
};

const dietPlanSlice = createSlice({
  name: "dietPlan",
  initialState,
  reducers: {
    resetGetDietPlanError: (state) => {
      state.error = false;
    },
    resetGetDatesToDisable: (state) => {
      state.getDatesToDisable.error = false;
    },
    clearReplacements: (state) => {
      state.replacements = [];
      state.replacementsMeta = {
        current_page: 1,
        last_page: 1,
        total: 0,
        user_has_favorites: state.replacementsMeta?.user_has_favorites ?? false,
      };
      state.replacementsError = null;
    },
    clearReplacementsAll: (state) => {
      state.replacements = [];
      state.replacementsMeta = {
        current_page: 1,
        last_page: 1,
        total: 0,
        user_has_favorites: false,
      };
      state.replacementsError = null;
      state.replacementsFilters = null;
    },
    resetDietPlanV1Error: (state) => {
      state.dietPlanV1.error = false;
    },
    clearDietPlanV1: (state) => {
      state.dietPlanV1.error = false;
      state.dietPlanV1.errorStatus = null;
      state.dietPlanV1.errorCode = null;
      state.dietPlanV1.dietPlan = null;
      state.dietPlanV1.shoppingList = null;
      state.dietPlanV1.lastFetched = null;
      state.dietPlanV1.lastFetchedShoppingList = null;
      state.dietPlanV1.lastFetchedPlanMode = null;
      // Invalidate any in-flight response so a late MC payload cannot repaint.
      state.dietPlanV1.currentRequestId = null;
      state.dietPlanV1.isLoading = false;
    },
    setPlanMode: (state, action) => {
      if (state.planMode === action.payload) return;
      state.planMode = action.payload;
      // Cached plan belongs to the previous mode — clear it so shouldFetch
      // fires against the correct endpoint. Also drop isLoading / requestId:
      // otherwise a pending MC call keeps shouldFetch=false and a late MC
      // fulfill can still look "current" for one frame.
      state.dietPlanV1.dietPlan = null;
      state.dietPlanV1.shoppingList = null;
      state.dietPlanV1.lastFetched = null;
      state.dietPlanV1.lastFetchedShoppingList = null;
      state.dietPlanV1.error = false;
      state.dietPlanV1.errorStatus = null;
      state.dietPlanV1.errorCode = null;
      state.dietPlanV1.lastFetchedPlanMode = null;
      state.dietPlanV1.currentRequestId = null;
      state.dietPlanV1.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    // --- Food Replacements ---
    builder.addCase(getFoodReplacements.pending, (state, action) => {
      // Only show full loading spinner on first page
      if (action.meta.arg.page === 1) {
        state.replacementsLoading = true;
        state.replacements = [];
      }
      state.replacementsError = null;
    });
    builder.addCase(getFoodReplacements.fulfilled, (state, action) => {
      state.replacementsLoading = false;
      const { data, meta, filters, isFirstPage } = action.payload;

      state.replacementsMeta = meta;

      // Only store filters from the first page (they don't change per page)
      if (isFirstPage && filters) {
        state.replacementsFilters = filters;
      }

      if (isFirstPage) {
        state.replacements = data;
      } else {
        state.replacements = [...state.replacements, ...data];
      }
    });
    builder.addCase(getFoodReplacements.rejected, (state, action) => {
      state.replacementsLoading = false;
      state.replacementsError = action.payload;
    });

    // ... (Keep existing builders for getDietPlan, getDietPlanV1, getDatesToDisable) ...
    builder.addCase(getDietPlan.pending, (state) => {
      state.isLoading = true;
      state.error = false;
    });
    builder.addCase(getDietPlan.fulfilled, (state, action) => {
      state.isLoading = false;
      state.dietPlan = action.payload.dietPlan;
      state.dates = action.payload.dates;
      state.shoppingList = action.payload.shoppingList;
      state.foods = action.payload.foods;
    });
    builder.addCase(getDietPlan.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(getDietPlanV1.pending, (state, action) => {
      state.dietPlanV1.isLoading = true;
      state.dietPlanV1.currentRequestId = action.meta.requestId;
      state.dietPlanV1.error = false;
      state.dietPlanV1.errorStatus = null;
      state.dietPlanV1.errorCode = null;
      // Drop only the payload this request will replace, so a shopping-list
      // fetch does not blank the meal plan (and vice versa). Clearing here
      // also stops a slow previous-mode response from painting under the
      // skeleton after a mode switch.
      if (action.meta.arg?.shoppingList) {
        state.dietPlanV1.shoppingList = null;
      } else {
        state.dietPlanV1.dietPlan = null;
      }
    });
    builder.addCase(getDietPlanV1.fulfilled, (state, action) => {
      // A newer request (or a mode switch that nulled currentRequestId) owns
      // the slot — ignore this payload entirely, including isLoading.
      if (action.meta.requestId !== state.dietPlanV1.currentRequestId) {
        return;
      }
      if (action.payload.planMode && action.payload.planMode !== state.planMode) {
        state.dietPlanV1.isLoading = false;
        state.dietPlanV1.currentRequestId = null;
        return;
      }
      state.dietPlanV1.isLoading = false;
      if (action.payload.type === "dietPlan") {
        state.dietPlanV1.dietPlan = action.payload.dietPlan;
        state.dietPlanV1.lastFetched = action.payload.date;
        state.dietPlanV1.lastFetchedPlanMode =
          action.payload.planMode ?? state.planMode;
      } else if (action.payload.type === "shoppingList") {
        state.dietPlanV1.shoppingList = action.payload.shoppingList;
        state.dietPlanV1.lastFetchedShoppingList = action.payload.date;
        state.dietPlanV1.lastFetchedPlanMode =
          action.payload.planMode ?? state.planMode;
      }
    });
    builder.addCase(getDietPlanV1.rejected, (state, action) => {
      if (action.payload?.stale) {
        return;
      }
      if (action.meta.requestId !== state.dietPlanV1.currentRequestId) {
        return;
      }
      if (action.payload?.planMode && action.payload.planMode !== state.planMode) {
        state.dietPlanV1.isLoading = false;
        state.dietPlanV1.currentRequestId = null;
        return;
      }
      state.dietPlanV1.isLoading = false;
      state.dietPlanV1.error =
        action.payload?.message ?? action.error?.message ?? "GenericError";
      state.dietPlanV1.errorStatus = action.payload?.status ?? null;
      state.dietPlanV1.errorCode = action.payload?.code ?? null;
      // Track which mode produced this error so errorForDate can match correctly.
      state.dietPlanV1.lastFetchedPlanMode =
        action.payload?.planMode ?? state.planMode;
      // A 400 means "no plan for this date" — a genuine answer, not a transient
      // failure. Mark lastFetched so shouldFetch stops re-triggering the call.
      if (action.payload?.status === 400) {
        const dateArg = action.meta?.arg?.date;
        if (dateArg) {
          state.dietPlanV1.lastFetched = dayjs(dateArg).format("YYYY-MM-DD");
        }
      }
    });

    builder.addCase(getDatesToDisable.pending, (state) => {
      state.getDatesToDisable.isLoading = true;
    });
    builder.addCase(getDatesToDisable.fulfilled, (state, action) => {
      state.getDatesToDisable.isLoading = false;
      state.getDatesToDisable.daysLimit = action.payload.daysLimit;
      state.getDatesToDisable.disableDates = action.payload.disableDates;
      state.getDatesToDisable.routes = action.payload.routes;
    });
    builder.addCase(getDatesToDisable.rejected, (state, action) => {
      state.getDatesToDisable.isLoading = false;
      state.getDatesToDisable.error = action.error.message;
    });
  },
});

export const {
  resetGetDietPlanError,
  resetGetDatesToDisable,
  resetDietPlanV1Error,
  clearDietPlanV1,
  clearReplacements,
  clearReplacementsAll,
  setPlanMode,
} = dietPlanSlice.actions;

export default dietPlanSlice.reducer;
